import React, { useEffect, useState, useContext } from "react";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { IJob } from "../../shared/interfaces/job/IJob";
import { JobService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  Row,
  Dropdown,
  Accordion,
} from "react-bootstrap";
import { Editor } from "react-draft-wysiwyg";
import { ContentState, convertFromHTML, EditorState } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import Select from "react-select";
// import { FloorTypeService } from "../floor-type/service";
import { JobCategoryService } from "../job-categories/service";
import { IMaterial } from "../../shared/interfaces/material/IMaterial";
import DualListBox from "react-dual-listbox";
import { MaterialService } from "../materials/service";
import { SettingsService } from "../settings/service";
import { AuthContext } from "../../context/AuthContext";
import { EnumsContext } from "../../context/EnumsContext";
import {
  formatGermanNumber,
  limitToTwoDecimals,
} from "../../shared/utils/functions";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

interface IJobMaterialItem {
  id?: number;
  rangeFrom?: number;
  rangeTo?: number;
  priceFactor?: number;
  rangeSellingPrice?: number;
  totalPrice?: number;
}

interface IMaterialData {
  id?: number;
  materialId: number;
  materialName?: string;
  consumption?: number;
  unit: string;
  unitLabel: string;
  sellingPrice?: number;
  totalPrice?: number;
  modifiedTotalPrice?: number;
  jobMaterialItemsData: IJobMaterialItem[];
}

const jobSchema = yup.object().shape({
  name: yup.string().required("validation.required"),
  internalName: yup.string().transform((value) => value || undefined).notRequired(),
  intro: yup.string(),
  parents: yup.array().of(yup.mixed()).notRequired(),
  jobCategory: yup.number().notRequired(),
  floorType: yup.number().notRequired(),
  jobPrice: yup.number().notRequired(),
  jobTotalPrice: yup.number().notRequired(),
  calculationType: yup.string().notRequired(),
  materialIds: yup.array().of(yup.number()).notRequired(),
});
const JobForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  // const [floorTypes, setFloorTypes] = useState<
  //   { value: number; label: string }[]
  // >([]);
  const [jobCategories, setJobCategories] = useState<
    { value: number; label: string }[]
  >([]);
  const [parentJobs, setParentJobs] = useState<
    { value: number; label: string }[]
  >([]);
  const [materialOptions, setMaterialOptions] = useState<IMaterial[]>([]);
  const [materialFactor, setMaterialFactor] = useState<number>(1);
  // Material rows live outside react-hook-form, so their edits are tracked separately
  const [materialsChanged, setMaterialsChanged] = useState(false);
  const skipJobTotalCalc = React.useRef(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<IJob>({
    defaultValues: {
      name: "",
      internalName: "",
      intro: "",
      parents: [],
      jobCategory: undefined,
      floorType: undefined,
      jobPrice: undefined,
      jobTotalPrice: undefined,
      calculationType: "AREA",
      materialIds: [],
    },
    resolver: yupResolver(jobSchema) as unknown as Resolver<IJob>,
  });

  const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(
    isDirty || materialsChanged,
  );

  const onSubmit = (job: IJob) => {
    const contentState = editorState.getCurrentContent();
    job.intro = stateToHTML(contentState);

    // Compute material data to send to backend
    const computedMaterialsData = materialsData.map((md) => {
      const sellingPrice = md.sellingPrice ?? 0;
      const consumption = md.consumption ?? 0;

      // Calculate totalPrice for each range item (for display/storage)
      const itemsWithTotal = md.jobMaterialItemsData.map((item) => ({
        id: item.id,
        rangeFrom: item.rangeFrom,
        rangeTo: item.rangeTo,
        priceFactor: item.priceFactor,
        rangeSellingPrice: Number((sellingPrice * (item.priceFactor ?? 0)).toFixed(2)),
        totalPrice: Number((consumption * sellingPrice * (item.priceFactor ?? 0)).toFixed(2)),
      }));

      // Material totalPrice is simply consumption * sellingPrice
      const materialTotalPrice = Number((consumption * sellingPrice).toFixed(2));

      return {
        id: md.id,
        materialId: md.materialId,
        consumption: md.consumption,
        unit: md.unit,
        sellingPrice: sellingPrice,
        totalPrice: materialTotalPrice,
        modifiedTotalPrice: md.modifiedTotalPrice || materialTotalPrice, // default to totalPrice if not set
        jobMaterialItemsData: itemsWithTotal,
      };
    });

    // Calculate jobTotalPrice (sum of all material totals or modified prices)
    const jobTotalPrice = computedMaterialsData.reduce(
      (sum, md) => sum + (md.modifiedTotalPrice || md.totalPrice || 0),
      0
    );

    const payload = {
      ...job,
      internalName: job.internalName?.trim() || null,
      jobTotalPrice: job.jobTotalPrice ?? jobTotalPrice,
      materialsData: computedMaterialsData,
    };
    const action = isEdit
      ? JobService.update(payload as any, Number(id))
      : JobService.create(payload as any);

    action
      .then(() => {
        SuccessToast(t(`job.${isEdit ? "successUpdate" : "successCreate"}`));
        navigate("/jobs");
      })
      .catch(ErrorToast);
  };

  // // Fetch floor types
  // useEffect(() => {
  //   const fetchFloorTypes = async () => {
  //     try {
  //       const response = await FloorTypeService.getAll({
  //         page: 1,
  //         perPage: 100,
  //       });
  //       const options = response.data.map((floorType: any) => ({
  //         value: floorType.id,
  //         label: floorType.name,
  //       }));
  //       setFloorTypes(options);
  //     } catch (error) {
  //       ErrorToast(error);
  //     }
  //   };

  //   fetchFloorTypes();
  // }, []);

  // Fetch job categories
  useEffect(() => {
    JobCategoryService.getAll({ page: 1, perPage: 1000 })
      .then((response) => {
        const options = response.data.map((cat: any) => ({
          value: cat.id,
          label: cat.name,
        }));
        setJobCategories(options);
      })
      .catch(ErrorToast);
  }, []);

  // Fetch parent jobs
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await JobService.get({
          page: 1,
          perPage: 1000,
        });
        const options = response.data.map((job: any) => ({
          value: job.id,
          label: job.name,
        }));
        setParentJobs(options);
      } catch (error) {
        ErrorToast(error);
      }
    };

    fetchJobs();
  }, []);

  useEffect(() => {
    if (id) {
      const fetchJob = async () => {
        try {
          const response = await JobService.getSingle(+id!);
          reset({
            ...response,
            parents: (response as any).parents?.map((p: any) => 
                typeof p === "object" && p !== null ? p.id : p
              ) || [],
            jobCategory:
              typeof response?.jobCategory === "object" &&
                response?.jobCategory !== null
                ? (response.jobCategory as any).id
                : response?.jobCategory,
            floorType:
              typeof response?.floorType === "object" &&
                response?.floorType !== null
                ? (response.floorType as any).id
                : response?.floorType,
            materialIds:
              (response as any).materialsData?.map((m: any) => m.material?.id).filter(Boolean) ||
              [],
          });
          // populate local materialsData if available from response
          if ((response as any).materialsData) {
            skipJobTotalCalc.current = true;
            setMaterialsData(
              (response as any).materialsData.map((m: any) => ({
                id: m.id,
                materialId: m.material?.id || m.materialId,
                materialName: m.material?.name || m.materialName,
                consumption: m.consumption,
                unit: m.unit,
                unitLabel: formatUnit(m.unit),
                sellingPrice: m.sellingPrice,
                totalPrice: m.totalPrice,
                modifiedTotalPrice: m.modifiedTotalPrice,
                jobMaterialItemsData: (m.jobMaterialItems || m.jobMaterialItemsData || []).map((item: any) => ({
                  id: item.id,
                  rangeFrom: item.rangeFrom,
                  rangeTo: item.rangeTo,
                  priceFactor: item.priceFactor,
                  totalPrice: item.totalPrice,
                })),
              })),
            );
          }
          const blocksFromHTML = convertFromHTML(response?.intro || "");
          const contentState = ContentState.createFromBlockArray(
            blocksFromHTML.contentBlocks,
            blocksFromHTML.entityMap,
          );
          setEditorState(EditorState.createWithContent(contentState));
        } catch (error) {
          ErrorToast(error);
        }
      };

      fetchJob();
    }
  }, [id]);

  useEffect(() => {
    MaterialService.getAll({ page: 1, perPage: 10000 })
      .then((response) => {
        console.log('Materials fetched:', response?.data?.length);
        console.log('Total pages:', response?.totalPages);
        console.log('Materials data:', response?.data);
        setMaterialOptions(response?.data);
      })
      .catch(ErrorToast);
  }, []);

  // Fetch settings to get materialFactor
  useEffect(() => {
    SettingsService.get()
      .then((response: any) => {
        setMaterialFactor(response?.materialFactor || 1);
      })
      .catch(ErrorToast);
  }, []);

  // Helper function to calculate selling price based on recommended retail price, supplier discount, and material factor
  const calculateSellingPrice = (material: IMaterial | undefined): number => {
    if (!material) return 0;

    // Prefer empfohlener Verkaufspreis (recommended retail price) directly as the selling price
    if (material.recommendedRetailPrice && material.recommendedRetailPrice > 0) {
      return Number(material.recommendedRetailPrice.toFixed(2));
    }

    // Fallback: use purchasePrice with materialFactor
    if (material.purchasePrice && material.purchasePrice > 0) {
      return Number((material.purchasePrice * materialFactor).toFixed(2));
    }

    // Last resort: apply factor to recommendedRetailPrice minus supplier discount
    const recommendedRetailPrice = material.recommendedRetailPrice || 0;
    const supplierDiscountPercent = (material as any)?.supplier?.specialDiscount ?? 0;

    const buyingPrice = recommendedRetailPrice * (1 - supplierDiscountPercent / 100);
    const sellingPrice = buyingPrice * materialFactor;

    return Number(sellingPrice.toFixed(2));
  };

  // local materialsData state: keep it separate from react-hook-form to avoid changing IJob type
  const [materialsData, setMaterialsData] = useState<IMaterialData[]>([]);
  const updateMaterialsData: typeof setMaterialsData = (value) => {
    setMaterialsChanged(true);
    setMaterialsData(value);
  };
  const watchedJobPrice = watch("jobPrice") as number | undefined;
  const { user } = useContext(AuthContext);
  const { enums } = useContext(EnumsContext);

  // Recalculate selling prices when materialFactor changes or materialOptions are loaded
  useEffect(() => {
    if (materialFactor && materialOptions.length > 0 && materialsData.length > 0) {
      setMaterialsData((prev) =>
        prev.map((md) => {
          const material = materialOptions.find((m) => m.id === md.materialId);
          const calculatedSellingPrice = calculateSellingPrice(material);
          // Only update if selling price is 0 or undefined (not manually set by user)
          if (!md.sellingPrice || md.sellingPrice === 0) {
            return { ...md, sellingPrice: calculatedSellingPrice };
          }
          return md;
        })
      );
    }
  }, [materialFactor, materialOptions]);

  // Unit options from backend enums
  const unitOptions: string[] = enums?.adminUnitType || [];
  const formatUnit = (u: string) => {
    const map: Record<string, string> = {
      m2: "m²",
      cm2: "cm²",
      m3: "m³",
    };
    return map[u] || u;
  };

  // Helper functions for managing jobMaterialItemsData
  const addRangeItem = (materialId: number) => {
    updateMaterialsData((prev) =>
      prev.map((p) =>
        p.materialId === materialId
          ? {
            ...p,
            jobMaterialItemsData: [
              ...p.jobMaterialItemsData,
              { rangeFrom: undefined, rangeTo: undefined, priceFactor: materialFactor },
            ],
          }
          : p
      )
    );
  };

  const removeRangeItem = (materialId: number, index: number) => {
    updateMaterialsData((prev) =>
      prev.map((p) =>
        p.materialId === materialId
          ? {
            ...p,
            jobMaterialItemsData: p.jobMaterialItemsData.filter((_, i) => i !== index),
          }
          : p
      )
    );
  };

  const updateRangeItem = (
    materialId: number,
    index: number,
    field: keyof IJobMaterialItem,
    value: number | undefined
  ) => {
    updateMaterialsData((prev) =>
      prev.map((p) =>
        p.materialId === materialId
          ? {
            ...p,
            jobMaterialItemsData: p.jobMaterialItemsData.map((item, i) =>
              i === index ? { ...item, [field]: value } : item
            ),
          }
          : p
      )
    );
  };

  // Sorting functions for materials with drag and drop
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    updateMaterialsData((prev) => {
      const newData = [...prev];
      const draggedItem = newData[draggedIndex];
      newData.splice(draggedIndex, 1);
      newData.splice(index, 0, draggedItem);
      return newData;
    });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Save material order to backend when materials are reordered in edit mode
  useEffect(() => {
    const saveMaterialOrder = async () => {
      if (!id || materialsData.length === 0) return;
      try {
        const ids = materialsData.map(md => md.id).filter((id): id is number => id !== undefined);
        if (ids.length === 0) return; // Don't save if no IDs (create mode)
        await JobService.sortMaterials(Number(id), { ids });
      } catch (error) {
        // Silent error - sorting is not critical
        console.error('Failed to save material order:', error);
      }
    };

    // Debounce to avoid multiple calls during rapid reordering
    const timeoutId = setTimeout(() => {
      if (materialsData.length > 0) {
        saveMaterialOrder();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [materialsData, id]);

  // Calculate jobTotalPrice whenever materialsData or jobPrice changes
  useEffect(() => {
    const materialsTotal = materialsData.reduce((sum, md) => {
      const consumption = md.consumption ?? 0;
      const sellingPrice = md.sellingPrice ?? 0;
      const materialTotal = consumption * sellingPrice;
      const effectivePrice = md.modifiedTotalPrice || materialTotal;
      return sum + effectivePrice;
    }, 0);

    if (skipJobTotalCalc.current) {
      skipJobTotalCalc.current = false;
      return;
    }
    setValue("jobTotalPrice", Number((materialsTotal + (watchedJobPrice || 0)).toFixed(2)));
  }, [materialsData, watchedJobPrice, setValue]);

  // Custom filter callback for DualListBox - filters by name, internal name, article number, supplier, and offer text
  const customFilterCallback = (option: IMaterial, filterInput: string) => {
    if (!filterInput) return true;

    // Split search input by spaces to support multiple search terms
    const searchTerms = filterInput.toLowerCase().trim().split(/\s+/).filter(term => term.length > 0);

    // Get searchable fields combined into one string
    const materialName = (option.name || '').toLowerCase();
    const internalName = (option.internalName || '').toLowerCase();
    const articleNumber = (option.articleNumber || '').toLowerCase();
    const supplierName = ((option as any).supplier?.name || '').toLowerCase();
    const offerText = (option.offerText || '').toLowerCase();

    // Combine all searchable content
    const searchableContent = `${materialName} ${internalName} ${articleNumber} ${supplierName} ${offerText}`;

    // Check if ALL search terms are found in the combined searchable content
    return searchTerms.every(term => searchableContent.includes(term));
  };

  return (
    <Card>
      <CardBody>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="g-3">
            <Col xl={4}>
              <Form.Group>
                <Form.Label className={"required"}>
                  {t("general.name")}
                </Form.Label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Form.Control
                      type="text"
                      isInvalid={!!errors?.name?.message?.length}
                      placeholder={t("general.enterName")}
                      onChange={onChange}
                      value={value}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name && t("validation.required")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xl={4}>
              <Form.Group controlId="internalName">
                <Form.Label>{t("general.internalName")}</Form.Label>
                <Controller
                  name="internalName"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Form.Control
                      type="text"
                      placeholder={t("general.enterInternalName")}
                      onChange={onChange}
                      value={value ?? ""}
                    />
                  )}
                />
                <Form.Text className="text-muted">
                  {t("general.internalNameHint")}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col sm={12} xl={4}>
              <Form.Group className="mb-3" controlId="parents">
                <Form.Label>{t("job.parentJob")}</Form.Label>
                <Controller
                  name="parents"
                  control={control}
                  render={({ field }) => (
                    <Select
                      isMulti
                      classNamePrefix="react-select"
                      className={`react-select-container ${errors.parents ? "is-invalid" : ""}`}
                      options={parentJobs.filter(job => job.value !== Number(id))}
                      value={
                        parentJobs.filter((option) =>
                          (field.value as number[])?.includes(option.value)
                        ) || []
                      }
                      onChange={(options) =>
                        field.onChange(options ? options.map(o => o.value) : [])
                      }
                      placeholder={t("job.selectParentJob")}
                      isClearable
                      closeMenuOnSelect={false}
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col sm={12} xl={4}>
              <Form.Group className="mb-3" controlId="jobCategory">
                <Form.Label>{t("job.jobCategory")}</Form.Label>
                <Controller
                  name="jobCategory"
                  control={control}
                  render={({ field }) => (
                    <Select
                      classNamePrefix="react-select"
                      className={`react-select-container ${errors.jobCategory ? "is-invalid" : ""}`}
                      options={jobCategories}
                      value={
                        jobCategories.find(
                          (option) => option.value === field.value,
                        ) || null
                      }
                      onChange={(option) =>
                        field.onChange(option ? option.value : undefined)
                      }
                      placeholder={t("job.selectJobCategory")}
                      isClearable
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col sm={12} xl={4}>
              <Form.Group className="mb-3" controlId="calculationType">
                <Form.Label>{t("job.calculationType")}</Form.Label>
                <Controller
                  name="calculationType"
                  control= {control}
                  render={({ field }) => (
                    <Select
                      classNamePrefix="react-select"
                      className={`react-select-container ${errors.calculationType ? "is-invalid" : ""}`}
                      options={(enums?.calculationType || []).map((type: string) => ({
                        value: type,
                        label: type,
                      }))}
                      value={
                        (enums?.calculationType || [])
                          .map((type: string) => ({ value: type, label: type }))
                          .find((option: any) => option.value === field.value) || null
                      }
                      onChange={(option) =>
                        field.onChange(option ? option.value : undefined)
                      }
                      placeholder={t("job.selectCalculationType")}
                      isClearable
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col sm={12} xl={4}>
              <Form.Group className="mb-3" controlId="jobPrice">
                <Form.Label>{t("job.jobPrice")}</Form.Label>
                <Controller
                  name="jobPrice"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      type="number"
                      min={0}
                      step={0.01}
                      placeholder={t("job.enterJobPrice")}
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        field.onChange(isNaN(val) ? undefined : val);
                      }}
                    />
                  )}
                />
              </Form.Group>
            </Col>
            {/* floorType field removed */}
            <Col sm={12} xl={4}>
              <Form.Group className="mb-3" controlId="isMaterialsHidden">
                <Form.Label>{t("job.hideMaterials")}</Form.Label>
                <Controller
                  name="isMaterialsHidden"
                  control={control}
                  render={({ field }) => (
                    <Form.Check
                      type="switch"
                      id="isMaterialsHidden-switch"
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      label={t("job.hideMaterialsLabel")}
                      className="mt-2"
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3" controlId="intro">
                <Form.Label>{t("general.intro")}</Form.Label>
                <Controller
                  name="intro"
                  control={control}
                  render={({ field }) => (
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={(editorState) => {
                        setEditorState(editorState);
                        field.onChange(
                          stateToHTML(editorState.getCurrentContent()),
                        );
                      }}
                      wrapperClassName="demo-wrapper"
                      editorClassName={`form-control ${errors.intro ? "is-invalid" : ""}`}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {t(errors.intro?.message as string)}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm={12}>
              <Controller
                name="materialIds"
                control={control}
                render={({ field }) => {
                  return (
                    // @ts-ignore
                    <DualListBox
                      options={materialOptions}
                      selected={(
                        materialOptions?.filter((material) =>
                          (field.value as number[])?.some(
                            (id: number) => material.id === id,
                          ),
                        ) ?? []
                      )?.map((material) => material.id)}
                      onChange={(selected: any) => {
                        // selected is array of material ids
                        field.onChange(selected);
                        const selectedIds = Array.isArray(selected)
                          ? selected
                          : [];
                        updateMaterialsData((prev) => {
                          const next = selectedIds.map((id: number) => {
                            const existing = prev.find(
                              (p) => p.materialId === id,
                            );
                            if (existing) return existing;

                            // Find the material to calculate selling price and pre-fill consumption/unit
                            const material = materialOptions.find(m => m.id === id);
                            const calculatedSellingPrice = calculateSellingPrice(material);

                            return {
                              materialId: id,
                              consumption: material?.consumption || undefined,
                              unit: material?.unit || enums?.adminUnitType?.[0] || "m2",
                              unitLabel: formatUnit(material?.unit || enums?.adminUnitType?.[0] || "m2"),
                              sellingPrice: calculatedSellingPrice,
                              totalPrice: 0,
                              modifiedTotalPrice: undefined,
                              jobMaterialItemsData: [],
                            };
                          });
                          return next;
                        });
                      }}
                      canFilter
                      filterCallback={customFilterCallback}
                      getOptionValue={(item: IMaterial) => item.id}
                      getOptionLabel={(item: IMaterial) => item?.name}
                      icons={{
                        moveToAvailable: <span>&lt;</span>,
                        moveAllToAvailable: <span>&laquo;</span>,
                        moveToSelected: <span>&gt;</span>,
                        moveAllToSelected: <span>&raquo;</span>,
                      }}
                      lang={{
                        availableHeader: t("job.availableMaterials"),
                        selectedHeader: t("job.assignedMaterials"),
                        // common prop names across versions
                        availableFilterPlaceholder: t("job.filterMaterials"),
                        availableFilterPlaceholer: t("job.filterMaterials"),
                        filterPlaceholder: t("job.filterMaterials"),
                        selectedFilterPlaceholder: t("job.filterMaterials"),
                      }}
                    />
                  );
                }}
              />
            </Col>
            {/* Render small forms for each selected material below DualListBox */}
            {materialsData.map((md, index) => {
              const material = materialOptions.find(
                (m) => m.id === md.materialId,
              );

              // Calculate material total price (simply consumption * sellingPrice)
              const consumption = md.consumption ?? 0;
              const sellingPrice = md.sellingPrice ?? 0;
              const materialTotalPrice = consumption * sellingPrice;

              return (
                <Col sm={12} key={md.materialId}>
                  <Card
                    className="mb-3"
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    style={{
                      cursor: 'move',
                      opacity: draggedIndex === index ? 0.5 : 1,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <i className="ti ti-grip-vertical text-muted" style={{ fontSize: '20px' }}></i>
                          <h5 className="mb-0">
                            {material?.name || md.materialName || md.materialId}
                          </h5>
                        </div>
                        <div className="d-flex align-items-end gap-3">
                          <div className="text-end">
                            <small className="text-muted">{t("job.totalPrice")}: </small>
                            <div className="fw-bold">{formatGermanNumber(materialTotalPrice)}</div>
                          </div>
                          <div className="text-end" style={{ width: '150px' }}>
                            <small className="text-muted">{t("job.modifiedTotalPrice")}: </small>
                            <Form.Control
                              type="number"
                              size="sm"
                              step="0.01"
                              className="text-end"
                              value={md.modifiedTotalPrice || Number(materialTotalPrice.toFixed(2))}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                updateMaterialsData((prev) =>
                                  prev.map((p) =>
                                    p.materialId === md.materialId
                                      ? { ...p, modifiedTotalPrice: isNaN(val) ? undefined : val }
                                      : p
                                  )
                                );
                              }}
                            />
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            type="button"
                            onClick={() => {
                              // Remove from materialsData
                              updateMaterialsData((prev) =>
                                prev.filter((p) => p.materialId !== md.materialId)
                              );
                              // Remove from form's materialIds (dualbox)
                              const currentIds = watch("materialIds") as number[] || [];
                              setValue(
                                "materialIds",
                                currentIds.filter((id) => id !== md.materialId)
                              );
                            }}
                          >
                            <i className="ti ti-trash"></i>
                          </Button>
                        </div>
                      </div>

                      {/* Material Information Accordion */}
                      <Accordion className="mb-3">
                        <Accordion.Item eventKey="info">
                          <Accordion.Header>
                            <i className="ti ti-info-circle me-2"></i>
                            {t("material.materialInformation")}
                          </Accordion.Header>
                          <Accordion.Body>
                            <Row className="g-2">
                              <Col md={6}>
                                <div className="mb-2">
                                  <strong>{t("material.name")}:</strong>
                                  <div className="text-muted">{material?.name || "-"}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-2">
                                  <strong>{t("material.articleNumber")}:</strong>
                                  <div className="text-muted">{material?.articleNumber || "-"}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-2">
                                  <strong>{t("material.supplier")}:</strong>
                                  <div className="text-muted">{(material as any)?.supplier?.name || "-"}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-2">
                                  <strong>{t("material.materialType")}:</strong>
                                  <div className="text-muted">{(material as any)?.materialType?.name || "-"}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-2">
                                  <strong>{t("material.costPrice")}:</strong>
                                  <div className="text-muted">{(material as any)?.costPrice && (material as any).costPrice > 0 ? formatGermanNumber((material as any).costPrice) : "-"}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-2">
                                  <strong>{t("material.recommendedRetailPrice")}:</strong>
                                  <div className="text-muted">{material?.recommendedRetailPrice && material.recommendedRetailPrice > 0 ? formatGermanNumber(material.recommendedRetailPrice) : "-"}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-2">
                                  <strong>{t("material.purchasePrice")}:</strong>
                                  <div className="text-muted">{material?.purchasePrice && material.purchasePrice > 0 ? formatGermanNumber(material.purchasePrice) : "-"}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-2">
                                  <strong>{t("material.supplierDiscount")}:</strong>
                                  <div className="text-muted">{(material as any)?.supplier?.specialDiscount ? `${(material as any).supplier.specialDiscount}%` : "-"}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-2">
                                  <strong>{t("material.ourSalePrice")}:</strong>
                                  <div className="text-muted">{material?.ourSalePrice && material.ourSalePrice > 0 ? formatGermanNumber(material.ourSalePrice) : "-"}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-2">
                                  <strong>{t("material.unit")}:</strong>
                                  <div className="text-muted">{material?.unit || "-"}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-2">
                                  <strong>{t("material.consumption")}:</strong>
                                  <div className="text-muted">{material?.consumption ? formatGermanNumber(material.consumption) : "-"}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-2">
                                  <strong>{t("material.unitPerPack")}:</strong>
                                  <div className="text-muted">{material?.unitPerPack || "-"}</div>
                                </div>
                              </Col>
                              {material?.width && (
                                <Col md={6}>
                                  <div className="mb-2">
                                    <strong>{t("material.width")}:</strong>
                                    <div className="text-muted">{formatGermanNumber(material.width)}</div>
                                  </div>
                                </Col>
                              )}
                              {material?.length && (
                                <Col md={6}>
                                  <div className="mb-2">
                                    <strong>{t("material.length")}:</strong>
                                    <div className="text-muted">{formatGermanNumber(material.length)}</div>
                                  </div>
                                </Col>
                              )}
                              {material?.height && (
                                <Col md={6}>
                                  <div className="mb-2">
                                    <strong>{t("material.height")}:</strong>
                                    <div className="text-muted">{formatGermanNumber(material.height)}</div>
                                  </div>
                                </Col>
                              )}
                              {material?.offerText && (
                                <Col md={12}>
                                  <div className="mb-2">
                                    <strong>{t("material.offerText")}:</strong>
                                    <div className="text-muted">{material.offerText}</div>
                                  </div>
                                </Col>
                              )}
                              {material?.surfaceTreatment && (
                                <Col md={12}>
                                  <div className="mb-2">
                                    <strong>{t("material.surfaceTreatment")}:</strong>
                                    <div className="text-muted">{material.surfaceTreatment}</div>
                                  </div>
                                </Col>
                              )}
                            </Row>
                          </Accordion.Body>
                        </Accordion.Item>
                      </Accordion>

                      <Row className="mb-2">
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label>
                              {t("job.consumption") || "Consumption"}
                            </Form.Label>
                            <Form.Control
                              type="number"
                              min={0}
                              step={0.01}
                              value={md.consumption ?? ""}
                              onChange={(e) => {
                                const limited = limitToTwoDecimals(
                                  e.target.value,
                                );
                                updateMaterialsData((prev) =>
                                  prev.map((p) =>
                                    p.materialId === md.materialId
                                      ? {
                                        ...p,
                                        consumption:
                                          limited === ""
                                            ? undefined
                                            : Number(limited),
                                      }
                                      : p,
                                  ),
                                );
                              }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label>{t("job.unit") || "Unit"}</Form.Label>
                            <Dropdown className="w-100">
                              <Dropdown.Toggle
                                as="button"
                                id={`dropdown-unit-${md.materialId}`}
                                className="form-control text-start"
                                variant="outline-secondary"
                                type="button"
                                disabled={Boolean(material?.unit)}
                              >
                                {md.unit
                                  ? formatUnit(md.unit)
                                  : t("general.unit")}
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                {unitOptions.map((unit) => (
                                  <Dropdown.Item
                                    key={unit}
                                    onClick={() =>
                                      updateMaterialsData((prev) =>
                                        prev.map((p) =>
                                          p.materialId === md.materialId
                                            ? {
                                              ...p,
                                              unit,
                                              unitLabel: formatUnit(unit),
                                            }
                                            : p,
                                        ),
                                      )
                                    }
                                    active={md.unit === unit}
                                  >
                                    {formatUnit(unit)}
                                  </Dropdown.Item>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          </Form.Group>
                        </Col>
                        {user && (
                          <>
                            <Col md={2}>
                              <Form.Group>
                                <Form.Label>
                                  {t("material.costPrice")}
                                </Form.Label>
                                <div className="form-control-plaintext">
                                  {(material as any)?.costPrice && (material as any).costPrice > 0 ? formatGermanNumber((material as any).costPrice) : "-"}
                                </div>
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group>
                                <Form.Label>
                                  {t("material.recommendedRetailPrice")}
                                </Form.Label>
                                <div className="form-control-plaintext">
                                  {formatGermanNumber(
                                    (material?.recommendedRetailPrice || 0) *
                                    (1 - ((material as any)?.supplier?.specialDiscount || 0) / 100)
                                  )}
                                </div>
                              </Form.Group>
                            </Col>
                          </>
                        )}
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label>
                              {t("job.sellingPrice")}
                            </Form.Label>
                            <Form.Control
                              type="number"
                              min={0}
                              step={0.01}
                              value={md.sellingPrice ?? ""}
                              onChange={(e) => {
                                const limited = limitToTwoDecimals(
                                  e.target.value,
                                );
                                updateMaterialsData((prev) =>
                                  prev.map((p) =>
                                    p.materialId === md.materialId
                                      ? {
                                        ...p,
                                        sellingPrice:
                                          limited === ""
                                            ? undefined
                                            : Number(limited),
                                      }
                                      : p,
                                  ),
                                );
                              }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* Dynamic Range Items */}
                      <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Form.Label className="h6 mb-0">
                            {t("job.priceRanges") || "Price Ranges"}
                          </Form.Label>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            type="button"
                            onClick={() => addRangeItem(md.materialId)}
                          >
                            + {t("job.addRange") || "Add Range"}
                          </Button>
                        </div>

                        {md.jobMaterialItemsData.map((item, index) => (
                          <Row key={index} className="mb-2 align-items-end">
                            <Col md={2}>
                              <Form.Group>
                                <Form.Label>
                                  {t("job.rangeFrom") || "Range From"}
                                </Form.Label>
                                <Form.Control
                                  type="number"
                                  min={0}
                                  step={1}
                                  value={item.rangeFrom ?? ""}
                                  onChange={(e) => {
                                    const value = e.target.value === "" ? undefined : Number(e.target.value);
                                    updateRangeItem(md.materialId, index, "rangeFrom", value);
                                  }}
                                  placeholder={t("job.enterRangeFrom") || "From"}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group>
                                <Form.Label>
                                  {t("job.rangeTo") || "Range To"}
                                </Form.Label>
                                <Form.Control
                                  type="number"
                                  min={0}
                                  step={1}
                                  value={item.rangeTo ?? ""}
                                  onChange={(e) => {
                                    const value = e.target.value === "" ? undefined : Number(e.target.value);
                                    updateRangeItem(md.materialId, index, "rangeTo", value);
                                  }}
                                  placeholder={t("job.enterRangeTo") || "To"}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group>
                                <Form.Label>
                                  {t("job.priceFactor") || "Price Factor"}
                                </Form.Label>
                                <Form.Control
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={item.priceFactor ?? ""}
                                  onChange={(e) => {
                                    const limited = limitToTwoDecimals(e.target.value);
                                    const value = limited === "" ? undefined : Number(limited);
                                    updateRangeItem(md.materialId, index, "priceFactor", value);
                                  }}
                                  placeholder={t("job.enterPriceFactor") || "Factor"}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group>
                                <Form.Label>{t("job.sellingPrice")}</Form.Label>
                                <div className="form-control-plaintext">
                                  {formatGermanNumber(
                                    (Number(md.sellingPrice) || 0) *
                                    (Number(item.priceFactor) || 0),
                                  )}
                                </div>
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group>
                                <Form.Label>{t("general.price")}</Form.Label>
                                <div className="form-control-plaintext">
                                  {formatGermanNumber(
                                    (Number(md.consumption) || 0) *
                                    (Number(md.sellingPrice) || 0) *
                                    (Number(item.priceFactor) || 0),
                                  )}
                                </div>
                              </Form.Group>
                            </Col>
                            <Col md={1}>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                type="button"
                                onClick={() => removeRangeItem(md.materialId, index)}
                              >
                                ×
                              </Button>
                            </Col>
                          </Row>
                        ))}

                        {md.jobMaterialItemsData.length === 0 && (
                          <div className="text-muted small">
                            {t("job.noRangesAdded") || "No price ranges added. Click 'Add Range' to create one."}
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
            <Col sm={12}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex gap-2">
                  <Button type="submit" variant="primary">
                    {t(`job.${isEdit ? "update" : "create"}`)}
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => guardedNavigate("/jobs")}>
                    {t("button.cancel")}
                  </Button>
                </div>
                <div className="text-end">
                  <span className="text-muted me-2">{t("job.jobTotalPrice")}:</span>
                  <strong
                    className="fs-5"
                    style={{
                      borderBottom: "2px dashed #aaa",
                      cursor: "text",
                      paddingBottom: 1,
                    }}
                    contentEditable
                    suppressContentEditableWarning
                    onFocus={(e) => {
                      const el = e.currentTarget;
                      el.textContent = String(watch("jobTotalPrice") ?? 0);
                      const range = document.createRange();
                      range.selectNodeContents(el);
                      window.getSelection()?.removeAllRanges();
                      window.getSelection()?.addRange(range);
                    }}
                    onBlur={(e) => {
                      const val = parseFloat(e.currentTarget.textContent?.replace(",", ".") ?? "");
                      if (!isNaN(val)) {
                        setValue("jobTotalPrice", val);
                      }
                      e.currentTarget.textContent = formatGermanNumber(watch("jobTotalPrice") ?? 0);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                  >
                    {formatGermanNumber(watch("jobTotalPrice") ?? 0)}
                  </strong>
                </div>
              </div>
            </Col>
          </Row>
        </Form>
        {unsavedChangesModal}
      </CardBody>
    </Card>
  );
};

export default JobForm;
